/**
 * sim.ts — the stable engine core. Pure functions, no React, no DOM.
 *
 * The engine knows nothing about food, kitchens, or delivery. It advances a
 * ModelState from one tick to the next, asserting the conservation
 * invariant (stock(t+1) = stock(t) + inflow - outflow) for every interior
 * node, and exposes helpers the UI needs (initial state, stock lookup).
 *
 * The "model" — what numbers actually appear in a tick — is the backend's job
 * (a Python model over WS, or a replay file). The engine applies ticks; it
 * doesn't generate them. This is the literal analogue of the bandit engine's
 * "stable core knows no domain" rule (`plans/contextual_bandit_engine.md` §1):
 * if `sim.ts` ever imports a scenario by name, the architecture is wrong.
 */

import type {
  Graph,
  NodeId,
  ScenarioBundle,
  ModelState,
  TickPayload,
} from './types';

/** Build the initial sim state from a bundle. Stocks seed from
 *  `initialStocks` (default 0); step starts at 0; lastTick is a zero-filled
 *  placeholder so the UI has something to render before the first tick lands. */
export function initialState(bundle: ScenarioBundle): ModelState {
  const stocks: Record<NodeId, number> = {};
  for (const node of bundle.graph.nodes) {
    stocks[node.id] = bundle.initialStocks?.[node.id] ?? 0;
  }
  return {
    step: 0,
    stocks,
    lastTick: emptyTick(bundle.graph),
  };
}

/** Apply one tick to the state. Returns a NEW state (no mutation — the spec's
 *  Phase 2 failure mode). Asserts the conservation invariant for interior
 *  nodes; throws on violation so bugs surface at the boundary, not in the UI.
 *
 *  "Interior" = the node has at least one in-edge OR one out-edge in the graph.
 *  Pure source/sink nodes (no edges either way) are exempt — they're dangles.
 *  Boundary nodes (sources with only out-edges, sinks with only in-edges) are
 *  NOT exempt: their stock still must satisfy the invariant, which is why the
 *  ghost_kitchen scenario keeps customers/supplier at stock=0 with inflow/outflow
 *  matching what the boundary allows. */
export function step(prev: ModelState, tick: TickPayload, graph: Graph): ModelState {
  // Drop out-of-order ticks. The WS adapter can deliver late; the engine
  // never goes backwards. (Phase 5 failure mode.)
  if (tick.step <= prev.step) {
    return prev;
  }

  const nextStocks: Record<NodeId, number> = { ...prev.stocks };
  const adjacency = computeAdjacency(graph);
  const nodeMeta = new Map(graph.nodes.map((n) => [n.id, n]));

  for (const [id, v] of Object.entries(tick.nodeValues)) {
    const adj = adjacency.get(id);
    if (!adj) continue; // node not in this graph — ignore (defensive)

    // Conservation applies only to interior nodes (both in- and out-edges)
    // AND not opted out via `conserves: false`. Boundary nodes — pure sources,
    // pure sinks, or nodes where inflow/outflow mix units — are exempt. The
    // scenario decides its own accounting boundary; the engine is domain-free.
    const meta = nodeMeta.get(id);
    const isInterior = adj.inEdges.length > 0 && adj.outEdges.length > 0;
    // Default true for interior nodes — except assemblers: a recipe consumes
    // N input units per output unit, so the identity mixes units and never
    // holds. An explicit `conserves` on the node always wins.
    const conserves = meta?.conserves ?? (meta?.kind !== 'assembler');
    if (!isInterior || !conserves) {
      nextStocks[id] = v.stock;
      continue;
    }

    const prevStock = prev.stocks[id] ?? 0;
    const expected = prevStock + v.inflow - v.outflow;
    if (Math.abs(expected - v.stock) > CONSERVATION_EPSILON) {
      throw new Error(
        `Conservation violated at node "${id}" step ${tick.step}: ` +
          `prev=${prevStock} + inflow=${v.inflow} - outflow=${v.outflow} = ${expected}, ` +
          `but tick says stock=${v.stock}.`,
      );
    }
    nextStocks[id] = v.stock;
  }

  return { step: tick.step, stocks: nextStocks, lastTick: tick };
}

/** Floating-point tolerance for the conservation check. $0.01 / meal —
 *  generous enough that rounding in derived metrics doesn't trip the check,
 *  tight enough that a real accounting bug (money from nowhere) surfaces. */
export const CONSERVATION_EPSILON = 0.01;

// ─── Internal helpers ───────────────────────────────────────────────────────
interface Adjacency {
  inEdges: string[];
  outEdges: string[];
}

function computeAdjacency(graph: Graph): Map<NodeId, Adjacency> {
  const m = new Map<NodeId, Adjacency>();
  for (const n of graph.nodes) m.set(n.id, { inEdges: [], outEdges: [] });
  for (const e of graph.edges) {
    m.get(e.source)?.outEdges.push(e.id);
    m.get(e.target)?.inEdges.push(e.id);
  }
  return m;
}

function emptyTick(graph: Graph): TickPayload {
  const nodeValues: TickPayload['nodeValues'] = {};
  const edgeFlows: TickPayload['edgeFlows'] = {};
  for (const n of graph.nodes) {
    nodeValues[n.id] = { inflow: 0, outflow: 0, stock: 0 };
  }
  for (const e of graph.edges) {
    edgeFlows[e.id] = 0;
  }
  return { step: 0, nodeValues, edgeFlows };
}
